import { useMemo, useState } from "react";
import clsx from "clsx";
import { BellOff, Paperclip, Plus, Users } from "lucide-react";
import { Avatar, Button, CountBadge } from "@dfws/ui";
import type { ChatChannel } from "@dfws/contracts";
import { updateSettings, useSettings } from "../../settings/store";
import { nameOf, openDm, presenceOf, useChannels, useChatPeople } from "./chatData";
import { dayLabel, timeLabel } from "./format";
import { NewGroupDialog } from "./GroupDialogs";
import s from "./Chat.module.css";

function preview(c: ChatChannel, me: string, nameFor: (e: string) => string): string {
  const m = c.lastMessage;
  if (!m) return c.kind === "group" ? `${c.members.length} members` : "No messages yet";
  const who = m.author === me ? "You: " : c.kind === "group" ? `${nameFor(m.author).split(" ")[0]}: ` : "";
  if (m.deleted) return `${who}Deleted message`;
  return who + (m.body || (m.file ? m.file.name : ""));
}

function when(iso: string): string {
  const d = new Date(iso);
  const label = dayLabel(d);
  return label === "Today" ? timeLabel(d) : label;
}

/** Conversations first; typing a name also finds colleagues you have not messaged yet. */
export function ChannelList({ me, activeId, onOpen }: { me: string; activeId?: string; onOpen: (channelId: string) => void }) {
  const channels = useChannels();
  const people = useChatPeople();
  const [q, setQ] = useState("");
  const [newGroup, setNewGroup] = useState(false);
  const [opening, setOpening] = useState<string | null>(null);
  const query = q.trim().toLowerCase();
  const nameFor = (e: string) => nameOf(e, people.data);

  const items = useMemo(() => (channels.data?.items ?? []).filter((c) => !query || c.name.toLowerCase().includes(query)), [channels.data, query]);
  const dmWith = useMemo(() => new Set((channels.data?.items ?? []).filter((c) => c.kind === "dm").flatMap((c) => c.members)), [channels.data]);
  const others = (people.data?.items ?? []).filter((p) => (query ? (p.name + " " + p.email).toLowerCase().includes(query) && !dmWith.has(p.email) : !dmWith.has(p.email)));

  const startDm = async (email: string) => {
    setOpening(email);
    try {
      const c = await openDm(email);
      setQ("");
      onOpen(c.id);
    } finally {
      setOpening(null);
    }
  };

  return (
    <div className={s.channelList}>
      <div className={s.listTools}>
        <input className={s.search} type="search" placeholder="Find a person or group" aria-label="Find a person or group" value={q} onChange={(e) => setQ(e.target.value)} />
        <Button size="sm" icon={<Plus />} onClick={() => setNewGroup(true)}>Group</Button>
      </div>
      <NotificationPrompt />
      <div className={s.listScroll}>
        {items.length > 0 && (
          <ul className={s.rows} aria-label="Conversations">
            {items.map((c) => {
              const other = c.kind === "dm" ? (c.members.find((m) => m !== me) ?? me) : null;
              const presence = other ? presenceOf(other, people.data) : undefined;
              const unread = c.unread > 0 && !c.muted;
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    className={clsx(s.row, c.id === activeId && s.rowActive, unread && s.rowUnread)}
                    onClick={() => onOpen(c.id)}
                    aria-current={c.id === activeId ? "true" : undefined}
                    aria-label={`${c.name}${presence ? `, ${presence}` : ""}${c.unread ? `, ${c.unread} unread` : ""}${c.muted ? ", muted" : ""}`}
                  >
                    {other ? <Avatar name={c.name} email={other} size={32} presence={presence} /> : <span className={s.groupIcon} aria-hidden><Users /></span>}
                    <span className={s.rowMain}>
                      <span className={s.rowTop}>
                        <span className={s.rowName}>{c.name}</span>
                        {c.lastMessage && <time className={s.rowTime}>{when(c.lastMessage.createdAt)}</time>}
                      </span>
                      <span className={s.rowBottom}>
                        <span className={s.rowPreview}>
                          {c.lastMessage?.file && !c.lastMessage.body && <Paperclip aria-hidden className={s.inlineIcon} />}
                          {preview(c, me, nameFor)}
                        </span>
                        {c.muted ? <BellOff aria-hidden className={s.inlineIcon} /> : <CountBadge count={c.unread} emphasis className={s.unreadBadge} />}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {others.length > 0 && (
          <>
            <h3 className={s.sectionTitle}>{items.length ? "People" : "Start a conversation"}</h3>
            <ul className={s.rows} aria-label="People">
              {others.map((p) => (
                <li key={p.email}>
                  <button type="button" className={s.row} onClick={() => void startDm(p.email)} disabled={opening === p.email} aria-label={`Message ${p.name}, ${p.presence}`}>
                    <Avatar name={p.name} email={p.email} size={32} presence={p.presence} />
                    <span className={s.rowMain}>
                      <span className={s.rowName}>{p.name}</span>
                      <span className={s.rowPreview}>{p.email}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
        {!items.length && !others.length && <p className={s.listEmpty}>{channels.isPending || people.isPending ? "Loading" : query ? "No one matches" : "No one else is here yet"}</p>}
      </div>
      {newGroup && (
        <NewGroupDialog
          onClose={() => setNewGroup(false)}
          onCreated={(c) => {
            setNewGroup(false);
            onOpen(c.id);
          }}
        />
      )}
    </div>
  );
}

function NotificationPrompt() {
  const { chatNotifications } = useSettings();
  const [perm, setPerm] = useState(() => (typeof Notification === "undefined" ? "denied" : Notification.permission));
  if (!chatNotifications || perm !== "default") return null;
  return (
    <div className={s.prompt}>
      <span>Get a desktop notification when someone messages you.</span>
      <span className={s.promptActions}>
        <Button size="sm" variant="primary" onClick={() => void Notification.requestPermission().then(setPerm)}>Turn on</Button>
        <Button size="sm" variant="ghost" onClick={() => updateSettings({ chatNotifications: false })}>Not now</Button>
      </span>
    </div>
  );
}
